import React, { useCallback, useMemo } from 'react';
import {
  Editable,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  withReact,
} from 'slate-react';
import { createEditor, Descendant } from 'slate';
import EditorElement from 'components/RichTextEditorField/components/EditorElement';
import EditorLeaf from 'components/RichTextEditorField/components/EditorLeaf';
import useDescription from './hooks/useDescription';
import { Button, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { t } from 'i18next';
import { safeStringify, sentryError } from 'utils';

import './DisplayRichTextString.styles.scss';
import './DisplayRichTextString.styles.responsive.scss';

type SlatePreviewProps = {
  className?: string;
  // JSON stringified slate editor value
  children?: string;
  maxLength?: number;
  disableShowMore?: boolean;
  modalTitle?: string;
};

const SlatePreview: React.FC<SlatePreviewProps> = (props) => {
  const {
    className,
    children,
    disableShowMore = false,
    modalTitle,
    maxLength,
  } = props;

  const editor = useMemo(() => withReact(createEditor()), []);
  const editorInModal = useMemo(() => withReact(createEditor()), []);

  const [cutDescription, parsedDescription, showMore] = useDescription(
    children,
    maxLength,
  );

  const [fullDescriptionModal, setFullDescriptionModal] =
    useCallbackRef<ModalRef>(null);

  const renderElement = useCallback(
    (props: RenderElementProps) => <EditorElement {...props} />,
    [],
  );

  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <EditorLeaf {...props} />,
    [],
  );

  if (parsedDescription) {
    const description = maxLength ? cutDescription : parsedDescription;

    return (
      <div className="display-rich-text-string">
        <Slate editor={editor} initialValue={description as Descendant[]}>
          <Editable
            className={className}
            readOnly
            renderElement={renderElement}
            renderLeaf={renderLeaf}
          />
          {showMore && !disableShowMore && (
            <Button variant="link" onClick={() => fullDescriptionModal.open()}>
              <span>{t('seeMore')}</span>
            </Button>
          )}
        </Slate>

        <Modal title={modalTitle} ref={setFullDescriptionModal}>
          <Slate
            editor={editorInModal}
            initialValue={parsedDescription as Descendant[]}
          >
            <Editable
              className={className}
              readOnly
              renderElement={renderElement}
              renderLeaf={renderLeaf}
            />
          </Slate>
        </Modal>
      </div>
    );
  }

  if (typeof children === 'string') {
    return <p>{children}</p>;
  }

  // Avoid crashing the app if the description is in an unexpected shape.
  sentryError(
    new Error(
      `Unprocessable description by Slate editor: ${safeStringify({
        children,
      })}`,
    ),
  );

  return null;
};

export default SlatePreview;
